import { useCallback } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-hot-toast';
import { errorHandler } from '../utils/errorHandler';
import { useDispatch, useSelector } from 'react-redux';
import { setSlackSettings, setSlackSettingsError, setSlackSettingsLoading } from '../redux/slices/organization';
import useOrganizationService from './organization';
import {
  setSlackAvailableNotifications,
  setSlackAvailableNotificationsLoading,
  setSlackChannels,
  setSlackTargetsError,
  setSlackTargetsLoading,
  setSlackTriggers,
  setSlackTriggersError,
  setSlackTriggersLoading,
  setSlackUsers
} from '../redux/slices/settings';
import {
  AvailableNotificationsResponse,
  NotificationTriggerRequest,
  NotificationTriggerResponse,
  NotificationTriggersListResponse,
  SlackConnectUrlResponse,
  SlackDisconnectResponse,
  SlackSettings,
  SlackSettingsUpdateRequest,
  SlackTargetsResponse,
  SyncSlackUsersResponse,
  SyncSlackUsersStatusResponse
} from "../pages/Settings/types";

const useSlackService = () => {
  const dispatch = useDispatch();
  const { fetchOrgIntegrations } = useOrganizationService();
  const { slackSettings } = useSelector((state: any) => state.organization);
  const {
    slackTriggers = []
  } = useSelector((state: any) => state.settings);

  const showError = useCallback((error: any) => {
    errorHandler.showError(error);
  }, []);

  const getSlackSettings = useCallback(async (silent?: boolean): Promise<void> => {
    if (!silent) dispatch(setSlackSettingsLoading(true));
    try {
      const response = await axios.get<SlackSettings>('/integration/slack_settings');
      dispatch(setSlackSettings(response?.data || null));
    } catch (error) {
      if (!silent) showError(error);
      dispatch(setSlackSettingsError(error));
    } finally {
      if (!silent) dispatch(setSlackSettingsLoading(false));
    }
  }, [dispatch, showError]);

  const getSlackConnectUrl = useCallback(async (): Promise<string> => {
    try {
      const response = await axios.get<SlackConnectUrlResponse>('/integration/slack/connect');
      return response?.data?.oauth_url || '';
    } catch (error) {
      showError(error);
      throw error;
    }
  }, [showError]);

  const syncSlackUsers = useCallback(async () => {
    try {
      const response = await axios.post<SyncSlackUsersResponse>('/integration/slack/sync_slack_users');
      if (response?.data?.message) {
        toast.success(response?.data?.message);
      }
    } catch (error) {
      showError(error);
      throw error;
    }
  }, [showError]);

  const syncSlackUsersStatus = useCallback(async () => {
    try {
      const response = await axios.get<SyncSlackUsersStatusResponse>('/integration/slack/sync_slack_users_status');
      return response?.data?.is_syncing;
    } catch (error) {
      showError(error);
      throw error;
    }
  }, [showError]);

  const updateSlackSettings = useCallback(async (isActive: boolean): Promise<void> => {
    if (slackSettings) {
      dispatch(setSlackSettings({
        ...slackSettings,
        is_active: isActive
      }));
    } else {
      dispatch(setSlackSettings({
        is_active: isActive,
        access_token: null,
        connection_status: false,
        team_id: null,
        team_name: null
      }));
    }

    try {
      await axios.post<any>('/integration/slack_settings', { is_active: isActive } as SlackSettingsUpdateRequest);

      await fetchOrgIntegrations();
    } catch (error) {
      showError(error);
      dispatch(setSlackSettings(slackSettings || null));
    }
  }, [dispatch, fetchOrgIntegrations, showError, slackSettings]);

  const disconnectSlack = useCallback(async (): Promise<void> => {
    try {
      const res = await axios.get<SlackDisconnectResponse>('/integration/slack/disconnect');
      toast.success(res?.data?.message || 'Slack disconnected');
      await getSlackSettings();
      await fetchOrgIntegrations();
    } catch (error) {
      showError(error);
      throw error;
    }
  }, [fetchOrgIntegrations, getSlackSettings, showError]);

  const getSlackTargets = useCallback(async (orgId: string): Promise<SlackTargetsResponse | null> => {
    dispatch(setSlackTargetsLoading(true));
    try {
      const response = await axios.get<SlackTargetsResponse>(`/organization/${orgId}/notifications/slack/targets`);
      const data = response.data;
      dispatch(setSlackChannels(data.channels || []));
      dispatch(setSlackUsers(data.users || []));
      dispatch(setSlackTargetsError(null));
      return data;
    } catch (error: any) {
      dispatch(setSlackTargetsError(error?.message || 'Failed to fetch Slack targets'));
      showError(error);
      return null;
    } finally {
      dispatch(setSlackTargetsLoading(false));
    }
  }, [dispatch, showError]);

  // Notification Triggers
  const createNotificationTrigger = useCallback(async (orgId: string, data: NotificationTriggerRequest): Promise<NotificationTriggerResponse | null> => {
    try {
      const response = await axios.post<NotificationTriggerResponse>(`/organization/${orgId}/notifications/trigger`, data);
      toast.success('Notification trigger created successfully');
      return response.data;
    } catch (error) {
      console.error("Error creating notification trigger:", error);
      showError(error);
      return null;
    }
  }, [showError]);

  const updateNotificationTrigger = useCallback(async (orgId: string, triggerId: number, data: NotificationTriggerRequest): Promise<NotificationTriggerResponse | null> => {
    try {
      const response = await axios.put<NotificationTriggerResponse>(`/organization/${orgId}/notifications/trigger/${triggerId}`, data);
      toast.success('Notification trigger updated successfully');
      return response.data;
    } catch (error) {
      console.error("Error updating notification trigger:", error);
      showError(error);
      return null;
    }
  }, [showError]);

  const deleteNotificationTrigger = useCallback(async (orgId: string, triggerId: number): Promise<boolean> => {
    try {
      await axios.delete(`/organization/${orgId}/notifications/trigger/${triggerId}`);
      toast.success('Notification trigger deleted successfully');
      return true;
    } catch (error) {
      console.error("Error deleting notification trigger:", error);
      showError(error);
      return false;
    }
  }, [showError]);

  const fetchNotificationTriggers = useCallback(async (orgId: string): Promise<NotificationTriggerResponse[]> => {
    dispatch(setSlackTriggersLoading(true));
    try {
      const response = await axios.get<NotificationTriggersListResponse>(`/organization/${orgId}/notifications/triggers`);
      const list = response.data.notification_triggers || [];
      dispatch(setSlackTriggers(list));
      dispatch(setSlackTriggersError(null));
      return list;
    } catch (error: any) {
      console.error("Error fetching notification triggers:", error);
      dispatch(setSlackTriggersError(error?.message || 'Failed to fetch triggers'));
      showError(error);
      return [];
    } finally {
      dispatch(setSlackTriggersLoading(false));
    }
  }, [dispatch, showError]);

  const fetchAvailableNotifications = useCallback(async (orgId: string): Promise<AvailableNotificationsResponse> => {
    dispatch(setSlackAvailableNotificationsLoading(true));
    try {
      const response = await axios.get<AvailableNotificationsResponse>(`/organization/${orgId}/notifications`);
      const payload: AvailableNotificationsResponse = {
        enabled: response.data?.enabled ?? false,
        notifications: response.data?.notifications || []
      };
      dispatch(setSlackAvailableNotifications(payload.notifications));
      return payload;
    } catch (error) {
      console.error("Error fetching available notifications:", error);
      showError(error);
      dispatch(setSlackAvailableNotifications([]));
      return {
        enabled: false,
        notifications: []
      };
    } finally {
      dispatch(setSlackAvailableNotificationsLoading(false));
    }
  }, [dispatch, showError]);

  const updateSlackNotificationStatus = useCallback(async (notificationEnabled: boolean): Promise<boolean> => {
    try {
      await axios.post('/integration/slack/update_notification_status', {
        notification_enabled: notificationEnabled
      });
      toast.success(`Slack notifications ${notificationEnabled ? 'enabled' : 'disabled'}`);
      return true;
    } catch (error) {
      console.error("Error updating slack notification status:", error);
      showError(error);
      return false;
    }
  }, [showError]);

  const deleteNotificationTriggerOptimistic = useCallback(async (orgId: string, triggerId: number): Promise<boolean> => {
    const previous = slackTriggers;
    const optimistic = previous.filter((t: any) => String(t.id) !== String(triggerId));
    try {
      dispatch(setSlackTriggers(optimistic));
      await axios.delete(`/organization/${orgId}/notifications/trigger/${triggerId}`);
      toast.success('Notification trigger deleted successfully');
      return true;
    } catch (error) {
      dispatch(setSlackTriggers(previous));
      console.error("Error deleting notification trigger:", error);
      showError(error);
      return false;
    }
  }, [slackTriggers]);

  const toggleNotificationTriggerOptimistic = useCallback(async (orgId: string, triggerId: number): Promise<boolean> => {
    const previous = slackTriggers;
    const trigger: any = previous.find((t: any) => String(t.id) === String(triggerId));
    if (!trigger) return false;
    const newStatus = trigger.status === 'enabled' ? 'disabled' : 'enabled';
    const optimistic = previous.map((t: any) =>
      String(t.id) === String(triggerId) ? { ...t, status: newStatus } : t
    );
    try {
      dispatch(setSlackTriggers(optimistic));
      await axios.put(`/organization/${orgId}/notifications/trigger/${triggerId}`, {
        ...trigger,
        status: newStatus
      } as any);
      toast.success('Notification trigger updated successfully');
      return true;
    } catch (error) {
      dispatch(setSlackTriggers(previous));
      console.error("Error updating notification trigger:", error);
      showError(error);
      return false;
    }
  }, [slackTriggers]);

  const testSlackNotification = useCallback(async (entityType: string, sendTo: Array<{ slack_id: string, target_type: string }>): Promise<boolean> => {
    try {
      const response = await axios.post<{ message: string, status?: string }>('/integration/slack/test_notification', {
        entity_type: entityType,
        send_to: sendTo
      });
      if (response?.data?.message) {
        toast.success(response.data.message);
      }
      return true;
    } catch (error) {
      console.error("Error testing Slack notification:", error);
      showError(error);
      return false;
    }
  }, [showError]);

  return {
    getSlackConnectUrl,
    disconnectSlack,
    getSlackSettings,
    syncSlackUsers,
    syncSlackUsersStatus,
    updateSlackSettings,
    getSlackTargets,
    createNotificationTrigger,
    updateNotificationTrigger,
    deleteNotificationTrigger,
    fetchNotificationTriggers,
    fetchAvailableNotifications,
    updateSlackNotificationStatus,
    deleteNotificationTriggerOptimistic,
    toggleNotificationTriggerOptimistic,
    testSlackNotification
  };
};

export default useSlackService;